import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_ENDPOINTS, getAuthHeaders } from '../services/api';
import { User } from '../types';

interface AuthContextType {
	user: User | null;
	loading: boolean;
	login: (email: string, password: string) => Promise<User | null>;
	signup: (email: string, password: string, name?: string, role?: string) => Promise<void>;
	logout: () => void;
	checkAuth: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};

interface AuthProviderProps {
	children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);

	const checkAuth = async (): Promise<User | null> => {
		try {
			const token = localStorage.getItem('access_token');
			if (!token) {
				setLoading(false);
				return null;
			}

			// Check authentication with token

			const response = await fetch(API_ENDPOINTS.auth.me, {
				headers: getAuthHeaders(),
			});

			if (response.ok) {
				const userData: User = await response.json();
				// User authenticated successfully
				setUser(userData);
				return userData;
			} else {
				// Token invalid, removing from storage
				localStorage.removeItem('access_token');
				setUser(null);
				return null;
			}
		} catch (error) {
			// Handle auth check error
			localStorage.removeItem('access_token');
			setUser(null);
			return null;
		} finally {
			setLoading(false);
		}
	};

	const login = async (email: string, password: string): Promise<User | null> => {
		// Attempting login
		const isMobileApp =
		/wv|flutter|flutterwebview/i.test(navigator.userAgent) ||
		window.location.search.includes("client_type=mobile");
		const response = await fetch(isMobileApp ? `${API_ENDPOINTS.auth.signin}?client_type=mobile` : API_ENDPOINTS.auth.signin, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ email, password }),
		});

		if (!response.ok) {
			const error = await response.json();
			// Handle login error
			throw new Error(error.detail || 'Login failed');
		}

		const data = await response.json();
		// Login successful

		localStorage.setItem('access_token', data.access_token);

		// Check auth to get full user data including role
		const authedUser = await checkAuth();
		return authedUser;
	};

	const signup = async (email: string, password: string, name?: string, role?: string) => {
		// Attempting signup

		const response = await fetch(API_ENDPOINTS.auth.signup, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ email, password, full_name: name, role }),
		});

		if (!response.ok) {
			const error = await response.json();
			// Handle signup error
			throw new Error(error.detail || 'Signup failed');
		}

		const data = await response.json();
		// Signup successful

		localStorage.setItem('access_token', data.access_token);

		// Check auth to get full user data including role
		await checkAuth();
	};

	const logout = () => {
		// Logging out user
		localStorage.removeItem('access_token');
		setUser(null);
	};

	useEffect(() => {
		checkAuth();
	}, []);

	const value: AuthContextType = {
		user,
		loading,
		login,
		signup,
		logout,
		checkAuth,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
